// Chat Service for Stasht Chat API

import { API_CONFIG } from "@/lib/api/config";
import { apiClient } from "@/lib/api/client";
import { CONFIG } from "@/config";

// Fast search response
export interface SearchResult {
  stasht_id: string;
  id: string;
  score: number;
  title: string;
  caption: string;
  location?: string;
  image_url?: string;
  metadata?: any;
}

export interface SearchFacetBucket {
  value: string;
  count: number;
}

export interface SearchFacets {
  overall_message_domain?: SearchFacetBucket[];
  entity_domain?: SearchFacetBucket[];
  entity_category?: SearchFacetBucket[];
  category_iabv2_2_depth0?: SearchFacetBucket[];
  category_iabv2_2_depth1?: SearchFacetBucket[];
  category_iabv2_2_depth2?: SearchFacetBucket[];
}

export interface SearchFilters {
  overall_message_intent?: string;
  overall_message_domain?: string;
  entity_domain?: string;
  entity_category?: string;
  category_iabv2_2_depth0?: string;
  category_iabv2_2_depth1?: string;
  category_iabv2_2_depth2?: string;
  category_iabv2_2_any?: string[];
  intent?: string;
  message_domain?: string;
  domain?: string;
}

export interface SearchResponse {
  total_found: number;
  results: SearchResult[];
  offset?: number;
  limit?: number;
  has_more?: boolean;
  facets?: SearchFacets;
  tag_counts?: Record<string, number>;
}

/** One page of a scoped search: matching stash or Stuff (entity) ids, as natively (StashSearch). */
export interface ScopedSearchPage {
  ids: string[];
  next_cursor: string | null;
  total?: number;
}

import { authService } from "@/services/auth.service";
import { getClientPlatform } from "@/platform";
import type { ChatOperationRequest } from "@/features/chat/chat-model";
export class ChatHistoryError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

const ASK_STREAM_URL_BY_ENV: Record<string, string | undefined> = {
  // The `live` alias URL (stack output AskStreamLiveFunctionUrl): provisioned
  // concurrency lives on the alias, not on $LATEST, so this is the warm path.
  production: "https://4gdftg5pwueaxeo76gsbjy5hwe0dzpxm.lambda-url.us-east-1.on.aws",
  staging: "https://ux43wrha5ubchxllzppoikmwma0kfzzf.lambda-url.us-east-1.on.aws",
};

class ChatService {
  private get baseUrl(): string {
    return API_CONFIG.BASE_URL;
  }
  private get askStreamingUrl(): string {
    const configuredStreamBase = CONFIG.API_ENV === "production" ? CONFIG.CHAT_STREAM_URL : undefined;
    const defaultStreamBase = ASK_STREAM_URL_BY_ENV[CONFIG.API_ENV] || this.baseUrl;
    const streamBase = (configuredStreamBase || defaultStreamBase || this.baseUrl).replace(/\/$/, "");
    return `${streamBase}/chat/ask/stream`;
  }
  private apiCall<T>(endpoint: string, body: unknown): Promise<T> {
    return apiClient.post<T>(endpoint, body);
  }

  /** Refresh credentials before sending; never replay a streamed prompt. */
  async stream<T>(
    body: unknown,
    signal: AbortSignal,
    consume: (response: Response, signal: AbortSignal) => Promise<T>,
  ): Promise<T> {
    const response = await this.chatFetch("/chat/ask/stream", body, signal, "application/x-ndjson", 150_000);
    return consume(response, signal);
  }

  private async chatFetch(
    path: string,
    body: unknown,
    signal: AbortSignal,
    accept = "application/json",
    timeout = 30_000,
  ) {
    const accountId = authService.getCurrentUser()?.user_id;
    const headers = await apiClient.getAuthHeaders({ includeApiKey: true });
    if (!accountId || accountId !== authService.getCurrentUser()?.user_id)
      throw new DOMException("Account changed", "AbortError");
    signal.throwIfAborted();
    const requestSignal = AbortSignal.any([signal, AbortSignal.timeout(timeout)]);
    return fetch(this.askStreamingUrl.replace(/\/chat\/ask\/stream$/, path), {
      method: "POST",
      cache: "no-store",
      credentials: "omit",
      signal: requestSignal,
      headers: {
        ...headers,
        "content-type": "application/json",
        accept,
        "x-stasht-client-platform": getClientPlatform(),
      },
      body: JSON.stringify(body),
    });
  }

  async history<T>(body: unknown, signal: AbortSignal): Promise<T> {
    const response = await this.chatFetch("/chat/conversations", body, signal);
    const result = await response.json();
    if (!response.ok)
      throw new ChatHistoryError(response.status, result.error || "Chats could not sync. Your local copy is kept.");
    if (result.schema_version !== "stasht.chat_history.v1")
      throw new Error("Chat sync returned an unreadable response.");
    return result as T;
  }

  async operations(
    body: ChatOperationRequest,
    signal: AbortSignal,
    currentAccount: () => boolean,
  ): Promise<{ operations: unknown }> {
    const accountId = authService.getCurrentUser()?.user_id;
    const headers = await apiClient.getAuthHeaders({ includeApiKey: true });
    if (!accountId || accountId !== authService.getCurrentUser()?.user_id)
      throw new DOMException("Account changed", "AbortError");
    signal.throwIfAborted();
    if (!currentAccount()) throw new DOMException("Account changed", "AbortError");
    const response = await fetch(`${this.baseUrl}/chat/ask/operations`, {
      method: "POST",
      cache: "no-store",
      signal: AbortSignal.any([signal, AbortSignal.timeout(45_000)]),
      headers: { ...headers, "content-type": "application/json", "x-stasht-client-platform": getClientPlatform() },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error("The result could not be confirmed. Check the result before trying again.");
    return response.json();
  }

  /**
   * Fast search for instant results (< 400ms)
   */
  async searchStashes(
    message: string,
    offset = 0,
    limit = 10,
    options?: { filters?: SearchFilters; includeFacets?: boolean; facetsOnly?: boolean },
  ): Promise<SearchResponse> {
    console.log("Searching stashes for:", message);
    const requestBody: Record<string, unknown> = {
      message,
      offset,
      limit,
    };

    if (options?.filters) {
      const normalizedFilters = Object.fromEntries(
        Object.entries(options.filters).flatMap(([key, value]) => {
          if (typeof value === "string") {
            const trimmed = value.trim();
            return trimmed ? [[key, trimmed]] : [];
          }
          if (Array.isArray(value)) {
            const cleaned = value.map(item => item.trim()).filter(Boolean);
            return cleaned.length > 0 ? [[key, cleaned]] : [];
          }
          return value ? [[key, value]] : [];
        }),
      );

      if (Object.keys(normalizedFilters).length > 0) {
        requestBody.filters = normalizedFilters;
      }
    }

    if (options?.includeFacets) {
      requestBody.include_facets = true;
    }
    if (options?.facetsOnly) {
      requestBody.facets_only = true;
    }

    const response = await this.apiCall<SearchResponse>("/chat/conversation/search", requestBody);
    console.log("Search results:", response);
    return response;
  }

  /** The account's stashes or Stuff matching a query, a page of ids at a time (native StashSearch, StuffSearch). */
  async searchIds(
    message: string,
    scope: "stashes" | "stuff",
    cursor?: string | null,
    limit = 20,
  ): Promise<ScopedSearchPage> {
    return this.apiCall<ScopedSearchPage>("/chat/conversation/search", {
      message,
      scope,
      cursor: cursor ?? undefined,
      limit,
    });
  }

  /** A collection's stashes matching a query, as ids, collaborators' included. */
  async searchCollection(message: string, collectionId: string): Promise<ScopedSearchPage> {
    return this.apiCall<ScopedSearchPage>("/chat/conversation/search", {
      message,
      scope: "stashes",
      collection_id: collectionId,
    });
  }
}

export const chatService = new ChatService();
